import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import type { ApiResponse, BrandSurfaceLocale } from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

interface GenerateBrandDnaDto {
  websiteUrl: string;
}

interface GenerateBrandDnaResponse extends ApiResponse {
  data: {
    dna: string;
  };
}

/** The Brand DNA must come back in the language the brand reads it in. */
export const brandDnaLanguage = (uiLanguage: string | undefined): BrandSurfaceLocale =>
  uiLanguage?.toLowerCase().startsWith('fr') ? 'fr' : 'en';

/**
 * Reads a website into a Brand DNA (`POST /dashboard/brands/me/dna/generate`),
 * without saving it. An unreachable site answers 422
 * `E_BRAND_DNA_WEBSITE_UNREACHABLE`.
 */
export async function requestBrandDna(
  data: GenerateBrandDnaDto,
  language: BrandSurfaceLocale
): Promise<GenerateBrandDnaResponse> {
  const response = await api.post('/dashboard/brands/me/dna/generate', { ...data, language });
  return response.data;
}

export function useGenerateBrandDnaAction() {
  const { t, i18n } = useTranslation();

  return useMutation<GenerateBrandDnaResponse, Error, GenerateBrandDnaDto>({
    mutationFn: (data) => requestBrandDna(data, brandDnaLanguage(i18n.language)),
    onError: (error) => {
      const code = isAxiosError(error) ? error.response?.data?.code : undefined;

      const description =
        code === 'E_BRAND_DNA_WEBSITE_UNREACHABLE'
          ? t('settings.brandDna.scan.errorUnreachable')
          : t('settings.brandDna.scan.errorGeneric');

      toaster.error({
        title: t('settings.brandDna.scan.errorTitle'),
        description,
      });
    },
  });
}
